import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Plus, Sparkles } from 'lucide-react';
import { useCommandes, useSettings } from '../api/hooks';
import ResumeCommande from '../components/commande/ResumeCommande';
import EventCard from '../components/EventCard';
import {
  Button,
  EmptyState,
  ErrorBox,
  PageHeader,
  SegmentedControl,
  Spinner,
  type SegmentedOption,
} from '../components/ui';

type Onglet = 'avenir' | 'passees';

const ONGLETS: readonly SegmentedOption<Onglet>[] = [
  { value: 'avenir', label: 'À venir' },
  { value: 'passees', label: 'Passées' },
];

export default function CommandesList() {
  const navigate = useNavigate();
  const { data, isPending, error } = useCommandes();
  // État des QR codes calculé sur l'heure serveur (décalable), comme dans l'app.
  const settings = useSettings();
  const serverNow = settings.data ? new Date(settings.data.serverNow) : new Date();
  const [onglet, setOnglet] = useState<Onglet>('avenir');

  const commandes = useMemo(() => {
    const now = Date.now();
    return (data ?? []).filter((commande) => {
      const date = new Date(commande.dateSeance).getTime();
      if (Number.isNaN(date)) return onglet === 'avenir';
      return onglet === 'avenir' ? date >= now : date < now;
    });
  }, [data, onglet]);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Commandes"
        actions={
          <>
            <Button variant="primary" onClick={() => navigate('/commandes/new')}>
              <Plus size={16} aria-hidden />
              Nouvelle commande
            </Button>
            <Button onClick={() => navigate('/scenarios')}>
              <Sparkles size={16} aria-hidden />
              Scénarios
            </Button>
          </>
        }
      />

      <SegmentedControl label="Filtrer les commandes" options={ONGLETS} value={onglet} onChange={setOnglet} />

      {error ? <ErrorBox error={error} /> : null}
      {isPending ? <Spinner /> : null}
      {!isPending && !error && commandes.length === 0 ? (
        <EmptyState>
          {onglet === 'avenir'
            ? 'Aucune commande à venir. Créez-en une ou lancez un scénario.'
            : 'Aucune commande passée.'}
        </EmptyState>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {commandes.map((commande) => (
          <EventCard
            key={commande.guid}
            to={`/commandes/${commande.guid}`}
            gradientKey={commande.libelleManif}
            title={commande.libelleManif}
            dateSeance={commande.dateSeance}
            statut={commande.statut}
            flagAction={commande.flagAction}
            flagActionCodeBarre={commande.flagActionCodeBarre}
            lieu={commande.libelleLieu}
            ville={commande.ville}
            footer={`Commande #${commande.idCommande} · ${commande.nbBillets} billet${commande.nbBillets > 1 ? 's' : ''}`}
          >
            <ResumeCommande commande={commande} qrDelivres={commande.nbBilletsAvecQr > 0} now={serverNow} />
          </EventCard>
        ))}
      </div>
    </div>
  );
}
